import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inboxApi, type Segment } from '@wa/shared';
import { Avatar } from '@/components/Avatar';

/**
 * Who is in the list — the reference's right-hand column.
 *
 * Until now a list could be counted and never looked at. This is the same query
 * the campaign engine freezes its audience from, so what is shown is who would
 * be sent to, rule-based lists included. Paged by contact id on scroll, because
 * a client's biggest list is exactly where OFFSET falls apart.
 *
 * "Add a member" is a search over contacts, not a form: the person already
 * exists, and the only thing to decide is which one.
 */
export function MembersPanel({ segment, onClose }: { segment: Segment; onClose?: () => void }) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const members = useInfiniteQuery({
    queryKey: ['segment-members', segment.id, debounced],
    queryFn: ({ pageParam }) => inboxApi.segmentMembers(segment.id, debounced || null, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextAfterId,
  });

  const remove = useMutation({
    mutationFn: (contactId: number) => inboxApi.removeSegmentMember(segment.id, contactId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['segment-members', segment.id] });
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
    },
  });

  const rows = members.data?.pages.flatMap((p) => p.items) ?? [];
  const isStatic = segment.kind === 0;

  return (
    <aside className="flex h-full min-h-0 flex-col border-l border-(--color-line) bg-(--color-surface)" aria-label="Members">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) px-3">
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Hide members" className="tap -ml-1 grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
            <IconClose />
          </button>
        )}
        <IconSearch />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search for members"
          aria-label="Search for members"
          className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)"
        />
      </div>

      <ul
        className="pane-scroll min-h-0 flex-1 overflow-y-auto"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200 && members.hasNextPage && !members.isFetchingNextPage) {
            void members.fetchNextPage();
          }
        }}
      >
        {members.isPending && <li className="px-4 py-10 text-center text-small text-(--color-muted)">Loading</li>}
        {members.isError && (
          <li className="px-6 py-10 text-center text-small text-(--color-bad)">Could not load the members of this list.</li>
        )}
        {!members.isPending && !members.isError && rows.length === 0 && (
          <li className="px-6 py-16 text-center text-body text-(--color-faint)">No members found</li>
        )}
        {rows.map((m) => (
          <li key={m.id} className="group flex items-center gap-3 border-b border-(--color-line-dim) px-3 py-2">
            <Avatar name={m.displayName} waId={m.waId} size={36} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body font-medium">{m.displayName ?? `+${m.waId}`}</span>
              <span className="block truncate text-small text-(--color-muted) tabular-nums">+{m.waId}</span>
            </span>
            {isStatic && (
              <button
                type="button"
                onClick={() => remove.mutate(m.id)}
                aria-label={`Remove ${m.displayName ?? m.waId} from this list`}
                className="tap grid size-8 place-items-center rounded-full text-(--color-faint) opacity-0 transition hover:bg-(--color-sunken) hover:text-(--color-bad) group-hover:opacity-100 focus-visible:opacity-100"
              >
                <IconClose />
              </button>
            )}
          </li>
        ))}
        {members.isFetchingNextPage && <li className="px-4 py-3 text-center text-small text-(--color-muted)">Loading more…</li>}
      </ul>

      {isStatic && (
        <footer className="shrink-0 border-t border-(--color-line)">
          {adding ? (
            <AddMember segmentId={segment.id} onDone={() => setAdding(false)} />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="tap flex w-full items-center justify-center gap-2 px-3 py-3 text-body font-medium text-(--color-ink) hover:bg-(--color-sunken)"
            >
              <IconAddPerson /> Add a member
            </button>
          )}
        </footer>
      )}
    </aside>
  );
}

function AddMember({ segmentId, onDone }: { segmentId: number; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);

  const found = useQuery({
    queryKey: ['contacts', 'pick', debounced],
    queryFn: () => inboxApi.contacts(new URLSearchParams({ q: debounced })),
    enabled: debounced.length >= 2,
  });

  const add = useMutation({
    mutationFn: (contactId: number) => inboxApi.addSegmentMembers(segmentId, [contactId]),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['segment-members', segmentId] });
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      onDone();
    },
  });

  return (
    <div className="p-2">
      <div className="flex items-center gap-2 rounded-(--radius-field) border border-(--color-brand) px-2">
        <IconSearch />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onDone(); }}
          placeholder="Name or number"
          aria-label="Find a contact to add"
          className="h-9 min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)"
        />
        <button type="button" onClick={onDone} aria-label="Cancel" className="tap grid size-8 place-items-center text-(--color-muted)"><IconClose /></button>
      </div>
      {debounced.length >= 2 && (
        <ul className="mt-1 max-h-56 overflow-y-auto rounded-(--radius-field) border border-(--color-line)">
          {(found.data?.items ?? []).slice(0, 8).map((c) => (
            <li key={c.id}>
              <button
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate(c.id)}
                className="tap flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-(--color-sunken)"
              >
                <Avatar name={c.displayName} waId={c.waId} size={28} />
                <span className="min-w-0 flex-1 truncate text-body">{c.displayName ?? `+${c.waId}`}</span>
                <span className="text-small text-(--color-muted) tabular-nums">+{c.waId}</span>
              </button>
            </li>
          ))}
          {found.data && found.data.items.length === 0 && (
            <li className="px-3 py-2 text-small text-(--color-muted)">Nobody matches.</li>
          )}
        </ul>
      )}
    </div>
  );
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconSearch = () => <svg viewBox="0 0 20 20" className="size-4.5 shrink-0 text-(--color-faint)" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.2" {...s} /><path d="m12.8 12.8 3.6 3.6" {...s} /></svg>;
const IconClose = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg>;
const IconAddPerson = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><circle cx="8" cy="7" r="3" {...s} /><path d="M2.5 16a5.5 5.5 0 0 1 11 0M15 8v5M12.5 10.5h5" {...s} /></svg>;
